import React, { useCallback } from 'react'
import { selectLogin, selectForgotPassword } from 'selectors/auth'
import * as SpinnerModule from 'components/utils/Spinner'
import { useForm } from 'components/utils/useForm'
import { login as loginAction, dismissLoginError, forgotPassword } from 'actions/auth'
import Button from 'components/utils/Button'
import FormGroup from 'components/utils/FormGroup'
import FormLabel from 'components/utils/FormLabel'
import FormControl from 'components/utils/FormControl'
import FormHelp from 'components/utils/FormHelp'
import Alert from 'components/utils/Alert'
import { useAppSelector, useAppDispatch } from 'store/hooks'


const Spinner = SpinnerModule.default || SpinnerModule

const validators = {
  username: {
    errors: {
      'Enter your username': (username: string) => username.length === 0,
    },
  },
  password: {
    errors: {
      'Enter your password': (password: string) => password.length === 0,
    },
  },
}

const LoginForm = () => {
  const { login, errors } = useAppSelector(selectLogin)
  const forgotPasswordState = useAppSelector(selectForgotPassword)
  const dispatch = useAppDispatch()

  const onSubmit = useCallback(
    (values: Record<string, string>) => {
      dispatch(loginAction(values as any) as any)
    },
    [dispatch]
  )

  const { handleFieldChange, handleSubmit, fieldValue, fieldMessage, validationState, formInvalid } = useForm({
    initialFields: { username: '', password: '' },
    validators,
    onSubmit,
  })

  const handleForgotPassword = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      e.preventDefault()
      e.stopPropagation()
      Promise.resolve()
        .then(() => dispatch(forgotPassword({ username: fieldValue('username') }) as any))
        .then((a: { error?: unknown }) => {
          if (!a.error) {
            window.location.href = '/resetPassword'
          }
        })
    },
    [dispatch, fieldValue]
  )

  const buttonText = login?.isFetching ? 'Loggin you in...' : 'Login'
  const buttonDisabled = formInvalid || login?.isFetching || (forgotPasswordState as any)?.isFetching

  const renderErrors = () => {
    if (!(errors?.length > 0)) {
      return null
    }
    return (
      <div>
        {errors.map((error: unknown, idx: number) => (
          <Alert key={idx} onDismiss={() => dispatch(dismissLoginError(idx))}>
            {String(error)}
          </Alert>
        ))}
      </div>
    )
  }

  return (
    <div>
      <form onSubmit={handleSubmit}>
        <FormGroup error={validationState('username') === 'error'}>
          <FormLabel>Username or Email</FormLabel>
          <FormControl
            required
            type="text"
            value={fieldValue('username')}
            placeholder="username..."
            onChange={handleFieldChange.bind(null, 'username')}
          />
          <FormHelp>{fieldMessage('username')}</FormHelp>
        </FormGroup>
        <FormGroup error={validationState('password') === 'error'}>
          <FormLabel>Password</FormLabel>
          <FormControl
            required
            type="password"
            value={fieldValue('password')}
            placeholder="password..."
            onChange={handleFieldChange.bind(null, 'password')}
          />
          <FormHelp>{fieldMessage('password')}</FormHelp>
        </FormGroup>
        <FormGroup>
          {!(forgotPasswordState as any)?.isFetching ? (
            <a href="#" onClick={handleForgotPassword}>
              Forgot Password
            </a>
          ) : null}
          {(forgotPasswordState as any)?.isFetching ? <Spinner show={true} /> : null}
        </FormGroup>
        <Button type="submit" disabled={buttonDisabled}>
          {buttonText}
        </Button>
      </form>
      {renderErrors()}
    </div>
  )
}

export default LoginForm
